import type { GetCvtDiagnosticDetailsRequest } from '@packages/clients';

import { getLogger } from '@packages/core/logging';

import type { CvtDiagnosticEntity, SbrDiagnosticEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';
import { fetchSbrResponseGraphData, toCvtDiagnosticEntity } from '../helpers';

const logger = getLogger('cvt');

/** The result of fetching diagnostic details */
export type FetchDiagnosticDetailsResult = {
    /** The CVT diagnostic details */
    data: CvtDiagnosticEntity;

    /** The SBR graph data associated with the diagnostic, if available */
    graphData?: SbrDiagnosticEntity['data'];
};

/**
 * Fetches the CVT diagnostic details for a given diagnostic ID, including associated SBR diagnostic details and graph data if available.
 */
export class FetchDiagnosticDetails extends StsBackendOperation {
    /**
     * Fetch the CVT diagnostic details for the given diagnostic ID, map the response to the CvtDiagnosticEntity.
     *
     * @param params The parameters for fetching the diagnostic details.
     * @returns The fetched diagnostic details, including CVT and SBR data if available.
     */
    async exec(params: GetCvtDiagnosticDetailsRequest): Promise<FetchDiagnosticDetailsResult | undefined> {
        const { diagnosticId } = params;
        try {
            const response = await this.client.fetchCvtDiagnosticDetails({ diagnosticId });

            if (!response?.success || !response.data) {
                return undefined;
            }

            const diagnosticDetails = toCvtDiagnosticEntity(response.data);

            if (!response.data?.sbrResponseDataUrl) {
                return { data: diagnosticDetails, graphData: [] };
            }

            const graphData = await fetchSbrResponseGraphData(response.data.sbrResponseDataUrl);

            return { data: diagnosticDetails, graphData };
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to fetch CVT diagnostic details';
            logger.error('Failed to fetch the CVT diagnostic details', { error: e });
            throw new Error(message);
        }
    }
}
